import { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Check, X, Zap, TrendingUp, Shield, Star, ArrowRight,
  HelpCircle, CreditCard, Clock, BarChart3, Users, Headphones
} from 'lucide-react';
import { billingService } from '../utils/api';

const PricingPage = () => {
  const [currency, setCurrency] = useState('INR');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const { isSignedIn } = useUser();
  const [currentPlanId, setCurrentPlanId] = useState(null);

  const plans = [
    {
      name: 'Free',
      description: 'Perfect for testing and small projects',
      planId: 'free',
      price: { INR: 0, USD: 0 },
      popular: false,
      color: 'gray',
      features: {
        requests: '1,000 requests/month',
        apiKeys: '1 API key',
        responseTime: '< 500ms avg response',
        support: 'Community support',
        uptime: '99% uptime SLA',
        analytics: 'Basic analytics',
        rateLimit: 'Standard rate limits',
        retention: '7 days log retention'
      },
      included: ['requests', 'apiKeys', 'responseTime', 'support', 'uptime', 'analytics'],
      excluded: ['rateLimit', 'retention']
    },
    {
      name: 'Starter',
      description: 'Great for growing applications',
      planId: 'starter',
      price: { INR: 299, USD: 3.99 },
      yearlyPrice: { INR: 2990, USD: 39.99 },
      popular: true,
      color: 'primary',
      features: {
        requests: '10,000 requests/month',
        apiKeys: 'Up to 5 API keys',
        responseTime: '< 500ms avg response',
        support: 'Email support (24h response)',
        uptime: '99.5% uptime SLA',
        analytics: 'Advanced analytics & logs',
        rateLimit: 'Higher rate limits',
        retention: '30 days log retention',
        streaming: 'Streaming API access'
      },
      included: ['requests', 'apiKeys', 'responseTime', 'support', 'uptime', 'analytics', 'rateLimit', 'retention', 'streaming'],
      excluded: []
    },
    {
      name: 'Professional',
      description: 'For production applications at scale',
      planId: 'professional',
      price: { INR: 999, USD: 12.99 },
      yearlyPrice: { INR: 9990, USD: 129.99 },
      popular: false,
      color: 'blue',
      features: {
        requests: '100,000 requests/month',
        apiKeys: '5+ API keys (unlimited)',
        responseTime: '< 300ms avg response',
        support: 'Priority email & chat support',
        uptime: '99.9% uptime SLA',
        analytics: 'Advanced analytics & insights',
        rateLimit: 'Custom rate limits',
        retention: '90 days log retention',
        streaming: 'Streaming API access',
        batch: 'Batch processing',
        webhook: 'Webhook notifications',
        customModel: 'Custom model tuning'
      },
      included: ['requests', 'apiKeys', 'responseTime', 'support', 'uptime', 'analytics', 'rateLimit', 'retention', 'streaming', 'batch', 'webhook', 'customModel'],
      excluded: []
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions for large organizations',
      planId: 'enterprise',
      price: { INR: 'Custom', USD: 'Custom' },
      popular: false,
      color: 'purple',
      features: {
        requests: 'Unlimited requests',
        apiKeys: 'Unlimited API keys',
        responseTime: '< 200ms avg response',
        support: '24/7 dedicated support',
        uptime: '99.99% uptime SLA',
        analytics: 'Custom analytics & reporting',
        rateLimit: 'No rate limits',
        retention: 'Custom log retention',
        streaming: 'Streaming API access',
        batch: 'Batch processing',
        webhook: 'Webhook notifications',
        customModel: 'Custom model training',
        onPremise: 'On-premise deployment',
        sla: 'Custom SLA guarantees',
        dedicated: 'Dedicated infrastructure'
      },
      included: ['requests', 'apiKeys', 'responseTime', 'support', 'uptime', 'analytics', 'rateLimit', 'retention', 'streaming', 'batch', 'webhook', 'customModel', 'onPremise', 'sla', 'dedicated'],
      excluded: []
    }
  ];

  const allFeatures = [
    { key: 'requests', icon: Zap, label: 'Monthly Requests' },
    { key: 'apiKeys', icon: Shield, label: 'API Keys' },
    { key: 'responseTime', icon: Clock, label: 'Response Time' },
    { key: 'support', icon: Headphones, label: 'Support' },
    { key: 'uptime', icon: TrendingUp, label: 'Uptime SLA' },
    { key: 'analytics', icon: BarChart3, label: 'Analytics' },
    { key: 'rateLimit', icon: Zap, label: 'Rate Limits' },
    { key: 'retention', icon: Clock, label: 'Log Retention' },
    { key: 'streaming', icon: Zap, label: 'Streaming API' },
    { key: 'batch', icon: Users, label: 'Batch Processing' },
    { key: 'webhook', icon: Zap, label: 'Webhooks' },
    { key: 'customModel', icon: Star, label: 'Custom Models' },
    { key: 'onPremise', icon: Shield, label: 'On-Premise' },
    { key: 'sla', icon: Shield, label: 'Custom SLA' },
    { key: 'dedicated', icon: Star, label: 'Dedicated Infrastructure' }
  ];

  const getPrice = (plan) => {
    if (plan.price[currency] === 'Custom') return 'Custom';
    if (plan.price[currency] === 0) return 'Free';
    
    const price = billingPeriod === 'yearly' && plan.yearlyPrice 
      ? plan.yearlyPrice[currency] 
      : plan.price[currency];
    
    return currency === 'INR' ? `₹${price}` : `$${price}`;
  };

  const getSavings = (plan) => {
    if (!plan.yearlyPrice || billingPeriod === 'monthly') return null;
    const monthlyCost = plan.price[currency] * 12;
    const yearlyCost = plan.yearlyPrice[currency];
    const savings = monthlyCost - yearlyCost;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { amount: savings, percentage };
  };

  useEffect(() => {
    let mounted = true;

    const loadCurrentPlan = async () => {
      if (!isSignedIn) {
        setCurrentPlanId(null);
        return;
      }

      try {
        const { data } = await billingService.getSubscription();
        if (mounted) {
          const normalized = data?.plan || null;
          setCurrentPlanId(normalized);
        }
      } catch (error) {
        console.error('Unable to fetch current plan:', error);
      }
    };

    loadCurrentPlan();

    return () => {
      mounted = false;
    };
  }, [isSignedIn]);

  const PricingCard = ({ plan, index }) => {
    const savings = getSavings(plan);
    const normalizedPlanId = plan.planId || plan.name?.toLowerCase();
    const isCurrentPlan = currentPlanId && currentPlanId === normalizedPlanId;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className={`relative rounded-2xl ${
          plan.popular
            ? 'bg-gradient-to-b from-primary-50 to-white dark:from-primary-900/20 dark:to-dark-900 border-2 border-primary-500 shadow-2xl shadow-primary-500/20'
            : 'bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800'
        } p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1`}
      >
        {isCurrentPlan && (
          <div className="absolute top-4 right-4">
            <span className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 px-3 py-1 rounded-full text-xs font-semibold">
              Your current plan
            </span>
          </div>
        )}
        {plan.popular && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <span className="bg-gradient-to-r from-primary-600 to-orange-500 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg flex items-center space-x-1">
              <Star className="w-4 h-4" />
              <span>Most Popular</span>
            </span>
          </div>
        )}

        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
            {plan.description}
          </p>

          <div className="mb-4">
            <div className="text-5xl font-bold gradient-text mb-2">
              {getPrice(plan)}
            </div>
            {plan.price[currency] !== 'Custom' && plan.price[currency] !== 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                per {billingPeriod === 'yearly' ? 'year' : 'month'}
              </div>
            )}
            {savings && (
              <div className="mt-2 text-sm font-semibold text-green-600 dark:text-green-400">
                Save {savings.percentage}% ({currency === 'INR' ? '₹' : '$'}{savings.amount})
              </div>
            )}
          </div>

          {plan.name === 'Enterprise' ? (
            <a
              href="mailto:contact@techycsr.dev"
              className={`btn-${plan.popular ? 'primary' : 'outline'} w-full flex items-center justify-center space-x-2`}
            >
              <span>Contact Sales</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          ) : plan.planId === 'free' ? (
            isCurrentPlan ? (
              <button
                disabled
                className="btn-outline w-full flex items-center justify-center space-x-2 opacity-60 cursor-not-allowed"
              >
                <span>Current Plan</span>
              </button>
            ) : (
              <Link
                to="/sign-up"
                className={`btn-${plan.popular ? 'primary' : 'outline'} w-full flex items-center justify-center space-x-2`}
              >
                <span>Get Started</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            )
          ) : (
            isCurrentPlan ? (
              <button
                disabled
                className="btn-outline w-full flex items-center justify-center space-x-2 opacity-60 cursor-not-allowed"
              >
                <span>Current Plan</span>
              </button>
            ) : (
              <Link
                to={`/checkout?plan=${plan.planId}&billing=${billingPeriod}&currency=${currency}`}
                className={`btn-${plan.popular ? 'primary' : 'outline'} w-full flex items-center justify-center space-x-2`}
              >
                <span>Subscribe Now</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            )
          )}
        </div>

        <div className="space-y-3">
          {allFeatures
            .filter(f => plan.included.includes(f.key) || plan.excluded.includes(f.key))
            .map((feature) => {
              const Icon = feature.icon;
              const isIncluded = plan.included.includes(feature.key);
              
              return (
                <div
                  key={feature.key}
                  className={`flex items-start space-x-3 ${
                    !isIncluded ? 'opacity-40' : ''
                  }`}
                >
                  {isIncluded ? (
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-medium">
                      {plan.features[feature.key]}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {feature.label}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-gray-50 to-white dark:from-dark-950 dark:to-dark-900">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8">
              Choose the perfect plan for your needs. All plans include our advanced AI-powered NSFW detection.
            </p>

            {/* Currency Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              <button
                onClick={() => setCurrency('INR')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  currency === 'INR'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-dark-800 hover:bg-gray-200 dark:hover:bg-dark-700'
                }`}
              >
                ₹ INR
              </button>
              <button
                onClick={() => setCurrency('USD')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  currency === 'USD'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-dark-800 hover:bg-gray-200 dark:hover:bg-dark-700'
                }`}
              >
                $ USD
              </button>
            </div>

            {/* Billing Period Toggle */}
            <div className="inline-flex items-center space-x-3 bg-gray-100 dark:bg-dark-800 p-1 rounded-xl">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white dark:bg-dark-900 shadow-sm'
                    : ''
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  billingPeriod === 'yearly'
                    ? 'bg-white dark:bg-dark-900 shadow-sm'
                    : ''
                }`}
              >
                Yearly
                <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-semibold">
                  Save 17%
                </span>
              </button>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {plans.map((plan, index) => (
              <PricingCard key={plan.name} plan={plan} index={index} />
            ))}
          </div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {[
                {
                  q: 'What happens if I exceed my monthly request limit?',
                  a: 'Your API will continue to work, but you\'ll be charged for overage at ₹0.50 per 100 requests for Starter and ₹0.30 per 100 requests for Professional. Free tier requests will be throttled after the limit.'
                },
                {
                  q: 'Can I upgrade or downgrade my plan anytime?',
                  a: 'Yes! You can change your plan at any time. Upgrades are instant, and downgrades take effect at the end of your current billing cycle.'
                },
                {
                  q: 'Is there a setup fee?',
                  a: 'No setup fees, no hidden charges. You only pay for what you use.'
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'We accept all major credit/debit cards, UPI, net banking, and international cards through Razorpay.'
                },
                {
                  q: 'Do you offer refunds?',
                  a: 'Yes, we offer a 7-day money-back guarantee. If you\'re not satisfied, we\'ll refund your payment, no questions asked.'
                },
                {
                  q: 'How does the Enterprise plan work?',
                  a: 'Enterprise plans are customized based on your specific needs. Contact our sales team to discuss volume discounts, custom SLAs, and dedicated infrastructure.'
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card"
                >
                  <div className="flex items-start space-x-4">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex-shrink-0">
                      <HelpCircle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{faq.q}</h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">{faq.a}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <div className="inline-flex items-center space-x-8 text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm">256-bit SSL Secure</span>
              </div>
              <div className="flex items-center space-x-2">
                <CreditCard className="w-5 h-5" />
                <span className="text-sm">Razorpay Secured</span>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5" />
                <span className="text-sm">99.9% Uptime</span>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center bg-gradient-to-r from-primary-600 to-orange-500 rounded-2xl p-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands of developers using our API for content moderation
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/sign-up"
                className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
              >
                <span>Start Free Trial</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="mailto:contact@techycsr.dev"
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Contact Sales
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
